import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useNavigate } from 'react-router-dom';
import { fetchLiveQuotes } from '../../services/apiClient';
import { useMarketPolling } from '../../hooks/useMarketPolling';
import { earningsService } from '../../services/earningsService';
import { wheelService } from '../../services/wheelService';
import { WheelDiscoveryRow, ExpiryOption } from '../../types/wheel';
import { fmt } from '../../utils/formatters';

const PAGE_SIZE = 20;

const timeAgo = (ts: string | null | undefined) => {
  if (!ts) return '';
  const mins = Math.round((Date.now() - new Date(ts).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ${mins % 60}m ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const DiscoverWheel: React.FC = () => {
  const [rows, setRows] = useState<WheelDiscoveryRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [earningsMap, setEarningsMap] = useState<Record<string, string>>({});
  const [expiries, setExpiries] = useState<ExpiryOption[]>([]);
  const [selectedExpiry, setSelectedExpiry] = useState('');
  const [hideRisky, setHideRisky] = useState(true);
  const [roiSort, setRoiSort] = useState<'asc' | 'desc'>('desc');
  const [lastRefreshed, setLastRefreshed] = useState<string | null>(null);
  const navigate = useNavigate();

  const sortedRows = [...rows].sort((a, b) => {
    const aVal = a.roi ? parseFloat(a.roi) : -Infinity;
    const bVal = b.roi ? parseFloat(b.roi) : -Infinity;
    return roiSort === 'desc' ? bVal - aVal : aVal - bVal;
  });

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const activeRef = useRef(true);
  useEffect(() => { activeRef.current = true; return () => { activeRef.current = false; }; }, []);

  // Load available expiries on mount, restore from localStorage
  useEffect(() => {
    wheelService.getExpiries().then((exps) => {
      setExpiries(exps);
      const stored = localStorage.getItem('wheel_preferred_expiry');
      const valid = exps.find((e) => e.date === stored);
      const defaultExp = valid || exps.find((e) => e.default) || exps[0];
      if (defaultExp) setSelectedExpiry(defaultExp.date);
    }).catch(() => {});
  }, []);

  const load = useCallback((p: number, expiry: string, safeOnly: boolean) => {
    if (!expiry) return;
    setLoading(true);
    const loadStart = performance.now();
    wheelService
      .getDiscoveryList(p, PAGE_SIZE, expiry, safeOnly)
      .then((data) => {
        const durationMs = Math.round(performance.now() - loadStart);
        import('../../services/firebase').then(({ logEvent }) => {
          logEvent('wheel_discovery_load', { duration_ms: durationMs, count: data.items.length });
        });
        if (!activeRef.current) return;
        setRows(data.items);
        setTotal(data.total);
        setPage(data.page);
        if (data.last_refreshed_at) setLastRefreshed(data.last_refreshed_at);
        // Patch in live prices after render
        const tickers = Array.from(new Set(data.items.map((r) => r.ticker)));
        fetchLiveQuotes(tickers).then((prices) => {
          if (!activeRef.current || Object.keys(prices).length === 0) return;
          setRows((prev) =>
            prev.map((r) => prices[r.ticker] != null
              ? { ...r, underlying_price: String(prices[r.ticker]) }
              : r
            ),
          );
        });
        // Backfill 12-week avg IV (slow historical query)
        wheelService.getAvgIv(expiry).then((avgIvMap) => {
          if (!activeRef.current || Object.keys(avgIvMap).length === 0) return;
          setRows((prev) =>
            prev.map((r) => avgIvMap[r.ticker] != null
              ? { ...r, avg_iv_12w: avgIvMap[r.ticker] }
              : r
            ),
          );
        });
      })
      .catch((e) => { if (activeRef.current) setError(e.message); })
      .finally(() => { if (activeRef.current) setLoading(false); });
  }, []);

  useEffect(() => { if (selectedExpiry) load(1, selectedExpiry, hideRisky); }, [selectedExpiry, hideRisky, load]);

  const handleExpiryChange = (newExpiry: string) => {
    setSelectedExpiry(newExpiry);
    localStorage.setItem('wheel_preferred_expiry', newExpiry);
  };

  // Fetch upcoming earnings dates to show badge on tickers with near-term earnings
  useEffect(() => {
    earningsService.getDiscoveryList()
      .then((rows) => {
        const map: Record<string, string> = {};
        const cutoff = Date.now() + 14 * 86400000;
        for (const r of rows) {
          const t = new Date(r.earnings_date).getTime();
          if (t >= Date.now() && t <= cutoff) {
            map[r.ticker] = r.earnings_date;
          }
        }
        setEarningsMap(map);
      })
      .catch(() => {});
  }, []);

  // Live price polling during market hours
  const pollTickers = useMemo(() => rows.map((r) => r.ticker), [rows]);
  useMarketPolling(pollTickers, useCallback((prices: Record<string, number>) => {
    setRows((prev) =>
      prev.map((r) => prices[r.ticker] != null ? { ...r, underlying_price: String(prices[r.ticker]) } : r),
    );
  }, []));

  if (loading && !rows.length) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!rows.length) return <Alert severity="info">No tickers in watchlist. Ask your admin to add tickers.</Alert>;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary">
            Sorted by annualized ROI. Strike selected at ~20 delta.
          </Typography>
          {lastRefreshed && (
            <Typography variant="caption" color="text.secondary">
              · Updated {timeAgo(lastRefreshed)}
            </Typography>
          )}
          <ToggleButtonGroup
            size="small"
            value={hideRisky ? 'safe' : 'all'}
            exclusive
            onChange={(_, v) => { if (v) setHideRisky(v === 'safe'); }}
          >
            <ToggleButton value="safe">Safe Only</ToggleButton>
            <ToggleButton value="all">Show All</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        {expiries.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <Select
              value={selectedExpiry}
              onChange={(e) => handleExpiryChange(e.target.value)}
            >
              {expiries.map((exp) => (
                <MenuItem key={exp.date} value={exp.date}>{exp.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>
      {/* Top pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            {page} / {totalPages}
          </Typography>
          <IconButton
            size="small"
            disabled={page <= 1 || loading}
            onClick={() => load(page - 1, selectedExpiry, hideRisky)}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            disabled={page >= totalPages || loading}
            onClick={() => load(page + 1, selectedExpiry, hideRisky)}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      <TableContainer component={Paper} elevation={1} sx={{ overflowX: 'auto', opacity: loading ? 0.6 : 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700 } }}>
              <TableCell>Ticker</TableCell>
              <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Strike</TableCell>
              <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Price</TableCell>
              <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                <Tooltip title="Strike at which total option buyer loss is maximized">
                  <span>Max Pain</span>
                </Tooltip>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active
                  direction={roiSort}
                  onClick={() => setRoiSort(roiSort === 'desc' ? 'asc' : 'desc')}
                >
                  ROI
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>IV</TableCell>
              <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                <Tooltip title="Average IV at this strike over last 12 weeks">
                  <span>Avg IV 12W</span>
                </Tooltip>
              </TableCell>
              <TableCell align="right"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows.map((r) => (
              <TableRow
                key={r.ticker}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(`/discover/wheel/${r.ticker}`, { state: { expiry: selectedExpiry } })}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Typography fontWeight={700}>{r.ticker}</Typography>
                    {earningsMap[r.ticker] && (
                      <Tooltip title={`Earnings ${fmt.date(earningsMap[r.ticker])}`}>
                        <Box sx={{
                          width: 20, height: 20, borderRadius: '50%',
                          border: 2, borderColor: 'warning.main',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          flexShrink: 0,
                        }}>
                          <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, color: 'warning.main', lineHeight: 1 }}>E</Typography>
                        </Box>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
                <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{fmt.strike(r.strike)}</TableCell>
                <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{fmt.currency(r.underlying_price)}</TableCell>
                <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  {r.max_pain ? (() => {
                    const mpBelow = r.strike && parseFloat(r.max_pain) < parseFloat(r.strike);
                    return (
                      <Tooltip title={mpBelow
                        ? `Max pain ($${parseFloat(r.max_pain).toFixed(0)}) is below strike ($${parseFloat(r.strike!).toFixed(0)}) — assignment risk ⚠`
                        : `Max pain ($${parseFloat(r.max_pain).toFixed(0)}) is above strike — favorable`
                      }>
                        <span>{fmt.currency(r.max_pain)} {mpBelow ? '⚠' : '✓'}</span>
                      </Tooltip>
                    );
                  })() : '—'}
                </TableCell>
                <TableCell align="right">
                  <RoiChip value={r.roi} ivElevated={!!r.iv && !!r.avg_iv_12w && parseFloat(r.iv) > parseFloat(r.avg_iv_12w)} />
                </TableCell>
                <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{fmt.iv(r.iv)}</TableCell>
                <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{fmt.iv(r.avg_iv_12w)}</TableCell>
                <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => navigate(`/discover/wheel/${r.ticker}`, { state: { expiry: selectedExpiry } })}
                  >
                    Create Wheel
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mt: 2 }}>
          <IconButton
            size="small"
            disabled={page <= 1 || loading}
            onClick={() => load(page - 1, selectedExpiry, hideRisky)}
          >
            <ChevronLeftIcon />
          </IconButton>
          <Typography variant="body2">
            Page {page} of {totalPages}
          </Typography>
          <IconButton
            size="small"
            disabled={page >= totalPages || loading}
            onClick={() => load(page + 1, selectedExpiry, hideRisky)}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>
      )}

      {/* Legend */}
      <Box sx={{ display: 'flex', gap: 2, mt: 1.5, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Chip label="ROI %" size="small" color="info" variant="outlined" />
          <Typography variant="caption" color="text.secondary">IV above 12-week average — premium is historically elevated</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Chip label="ROI %" size="small" variant="outlined" />
          <Typography variant="caption" color="text.secondary">IV at or below 12-week average</Typography>
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Typography variant="caption" color="text.secondary">
        <strong>How ROI is projected:</strong> The strike nearest to 20 delta is selected for each expiry.
        ROI = (option mid-price ÷ strike) × (365 ÷ DTE), annualized assuming the put expires worthless and
        the full strike is held as collateral. This is a best-case projection — it does not account for
        early assignment, rolling costs, or gap risk.
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
        Max Pain is computed hourly from the latest option chain snapshot. Price generally tends to move towards the max pain price by the expiration date.
        If max pain is below the put strike (marked with ⚠), the stock may drift down toward max pain and you could get assigned — these may not be worth wheeling. A ✓ means max pain is above your strike, which is favorable.
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
        ROI highlighted in <Chip label="blue" size="small" color="info" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} /> indicates IV is elevated above the 12-week average — higher premium but also higher risk. Elevated IV often signals an upcoming event (earnings, FDA, etc.) and may not be worth selling into unless the difference is small.
      </Typography>
    </Box>
  );
};

const RoiChip: React.FC<{ value: string | null; ivElevated: boolean }> = ({ value, ivElevated }) => {
  if (!value) return <Typography variant="body2" color="text.secondary">—</Typography>;
  return <Chip label={fmt.percent(value)} size="small" color={ivElevated ? 'info' : 'default'} variant="outlined" />;
};

export default DiscoverWheel;
